import React, { useCallback, useMemo, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, alpha, Box, Button, Chip, Divider, LinearProgress, Skeleton, Tooltip, Typography } from '@mui/material';
import DoNotDisturbOnOutlinedIcon from '@mui/icons-material/DoNotDisturbOnOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import PhoneDisabledOutlinedIcon from '@mui/icons-material/PhoneDisabledOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import PhoneForwardedOutlinedIcon from '@mui/icons-material/PhoneForwardedOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';

import { useToast } from '../../../common/hooks/useToast';
import { Item } from '../../../styles/common';
import { CrmPageFrame } from '../CrmPageFrame';
import { contactPath, crmReturnState } from '../crmPaths';
import { queuePathFor } from '../callQueue/callQueue';
import { BatchUpdateDialog } from '../batch/BatchUpdateDialog';
import { forgetBatchData } from '../batch/batchUpdate';
import { BulkAction, forgetContactData, people } from '../tagging/tagging';
import { BulkActionBar } from './bulk/BulkActionBar';
import { BulkChangeDialog } from './bulk/BulkChangeDialog';
import {
  NO_SELECTION,
  pageState,
  Selection,
  SelectionChange,
  isSelected,
  selectionFilter,
  toggleRow,
  togglePage,
} from './bulk/selection';
import { AddContactDialog } from './AddContactDialog';
import { ColumnChooser, ContactPager } from './ContactListControls';
import { ContactSearchBox } from './ContactSearchBox';
import { ContactTable } from './ContactTable';
import { SaveFilterDialog } from './SaveFilterDialog';
import { FilterBar } from './filters/FilterBar';
import { ConsentCounts, FacetOption, FilterOptions, ListOption, TagOption } from './filters/types';
import { CRM_CONTACT_CONSENT_COUNTS, CRM_CONTACT_FILTER_OPTIONS } from './contacts.queries';
import {
  ConsentKey,
  ContactListState,
  CrmContactTotal,
  describeListError,
  describeSort,
  describeTotal,
  EMPTY_STATE,
  errorCodeOf,
  errorFieldOf,
  hasFilters,
  INITIAL_COLUMNS,
  nextSort,
  normaliseColumns,
  parseListState,
  toSearchParams,
  withFilterChange,
} from './contactList';
import { useContactPage } from './useContactPage';

/** Per browser, like any view preference: not in the URL, so a shared link shows the recipient's own columns. */
export const COLUMNS_STORAGE_KEY = 'ilbp.crm.contacts.columns';

const readColumns = (): Array<string> => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(COLUMNS_STORAGE_KEY) ?? 'null');
    return Array.isArray(stored) ? normaliseColumns(stored) : INITIAL_COLUMNS;
  } catch {
    return INITIAL_COLUMNS;
  }
};

const storeColumns = (columns: Array<string>) => {
  try {
    window.localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(columns));
  } catch {
    // Private windows and blocked storage: the choice lasts for this visit only.
  }
};

const n = (value: number) => value.toLocaleString('en-US');

/** The consent breakdown above the table; each one, clicked, shows only those contacts. */
const CONSENT_STATS: Array<{ key: ConsentKey; label: string; tone: 'success' | 'error'; Icon: typeof MailOutlineIcon }> = [
  { key: 'canEmail', label: 'Can email', tone: 'success', Icon: MailOutlineIcon },
  { key: 'canCall', label: 'Can call', tone: 'success', Icon: PhoneOutlinedIcon },
  { key: 'canText', label: 'Can text', tone: 'success', Icon: SmsOutlinedIcon },
  { key: 'doNotCall', label: 'Do not call', tone: 'error', Icon: PhoneDisabledOutlinedIcon },
  { key: 'doNotContact', label: 'Do not contact', tone: 'error', Icon: DoNotDisturbOnOutlinedIcon },
];

const ConsentStats = ({
  counts,
  state,
  onToggle,
}: {
  counts: ConsentCounts;
  state: ContactListState;
  onToggle: (key: ConsentKey) => void;
}) => (
  <>
    {CONSENT_STATS.map(({ key, label, tone, Icon }) => {
      const only = state.consent[key] === true;
      const share = counts.total ? Math.round((counts[key] / counts.total) * 100) : 0;
      return (
        <Tooltip
          key={key}
          // A description, not the name: the chip's own words name it.
          describeChild
          title={only ? 'Showing only these. Click to show everyone again.' : `${share}% of these contacts. Click to show only them.`}
        >
          <Chip
            size="small"
            clickable
            aria-pressed={only}
            onClick={() => onToggle(key)}
            icon={<Icon />}
            label={`${label} ${n(counts[key])}`}
            color={tone}
            variant={only ? 'filled' : 'outlined'}
            sx={(theme) => ({
              fontWeight: 500,
              fontVariantNumeric: 'tabular-nums',
              ...(only ? {} : { bgcolor: alpha(theme.palette[tone].main, 0.04), borderColor: alpha(theme.palette[tone].main, 0.3) }),
              '& .MuiChip-icon': { fontSize: 16 },
            })}
          />
        </Tooltip>
      );
    })}
  </>
);

/** Rows in the table's shape while the first page loads. */
const LoadingRows = () => (
  <Box role="status" aria-label="Loading contacts…" sx={{ px: 2, py: 0.5 }}>
    {Array.from({ length: 8 }, (_, index) => (
      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.25, borderBottom: '1px solid #eef1f5' }}>
        <Skeleton variant="circular" width={30} height={30} />
        <Skeleton variant="text" sx={{ flex: '0 0 16%' }} />
        <Skeleton variant="text" sx={{ flex: '0 0 22%' }} />
        <Skeleton variant="text" sx={{ flex: '0 0 12%' }} />
        <Skeleton variant="text" sx={{ flex: 1 }} />
      </Box>
    ))}
  </Box>
);

/**
 * The people database (card 18): every CRM contact, with columns, paging, sort,
 * free-text search, and filters on tag, list, consent, country, dates, support
 * level, point person, source and stage.
 *
 * Every choice is in the URL's query string, so the way back from a contact
 * (crmReturnState) lands on the list exactly as it was left. Requests, per
 * CRM_CONTACT_QUERY_FRONTEND.md §16:
 * - on opening: the page with its total, the consent counts, and the tag,
 *   list and country choices, the last cached for the visit;
 * - on a search or filter change: the first page with its total, and the counts;
 * - on next or previous: the page alone, by cursor;
 * - on a sort change: the first page alone;
 * - on first opening another filter's panel: its choices, once.
 *
 * Rows can be ticked, or everyone the filter matches selected, and then added
 * to a tag or list or taken off one (card 19, BulkChangeDialog). A selection
 * belongs to its filter: a new search or filter clears it.
 */
export const CrmContacts = () => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const state = useMemo(() => parseListState(params), [params]);
  const [columns, setColumns] = useState<Array<string>>(readColumns);

  const show = useCallback(
    (next: ContactListState) => setParams(toSearchParams(next)),
    [setParams]
  );
  const change = useCallback(
    (partial: Partial<ContactListState>) => show(withFilterChange(state, partial)),
    [show, state]
  );

  const { filter, rows, loading, error, hasNextPage, total: pageTotal } = useContactPage(state, columns);

  // The selection, kept with the filter it was made under: a new filter starts with none.
  const filterKey = JSON.stringify(filter ?? null);
  const [picked, setPicked] = useState<{ key: string; selection: Selection; capped: boolean }>({
    key: filterKey,
    selection: NO_SELECTION,
    capped: false,
  });
  const selection = picked.key === filterKey ? picked.selection : NO_SELECTION;
  const select = (change: SelectionChange) => setPicked({ key: filterKey, ...change });
  const [bulkAction, setBulkAction] = useState<BulkAction | null>(null);
  const [batching, setBatching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const toast = useToast();
  const client = useApolloClient();
  const pageIds = rows.map((row) => row.id);

  const counts = useQuery<{ crmContactCounts: ConsentCounts }>(CRM_CONTACT_CONSENT_COUNTS, {
    variables: filter ? { filter } : {},
  });
  const consent = counts.data?.crmContactCounts;

  const optionsQuery = useQuery<{
    crmTags: Array<TagOption>;
    countries: Array<FacetOption>;
    lists: { total: number; items: Array<ListOption> };
  }>(CRM_CONTACT_FILTER_OPTIONS);
  const options: FilterOptions | undefined = optionsQuery.data && {
    tags: optionsQuery.data.crmTags,
    countries: optionsQuery.data.countries,
    lists: optionsQuery.data.lists.items,
    listTotal: optionsQuery.data.lists.total,
  };

  // The counts are exact; the list's own total is an estimate above 10,000.
  const total: CrmContactTotal | undefined = consent ? { count: consent.total, exact: true } : pageTotal;
  const matchCount = total ? `${describeTotal(total)} match` : 'Counting…';

  const searchError =
    errorCodeOf(error) === 'CRM_INVALID_QUERY' && errorFieldOf(error)?.startsWith('filter.search')
      ? error?.graphQLErrors[0]?.message
      : undefined;

  const openContact = (contactId: number) => navigate(contactPath(contactId), { state: crmReturnState(location) });

  const chooseColumns = (next: Array<string>) => {
    setColumns(next);
    storeColumns(next);
  };

  const clearAll = () => show({ ...EMPTY_STATE, sort: state.sort, pageSize: state.pageSize });

  const toggleConsent = (key: ConsentKey) => {
    const next = { ...state.consent };
    if (next[key] === true) delete next[key];
    else next[key] = true;
    change({ consent: next });
  };

  const filtered = hasFilters(state);
  const firstLoad = loading && rows.length === 0;

  const closeBulk = (changed: boolean) => {
    setBulkAction(null);
    if (!changed) return;
    // Who has which tag or list has changed: every page, count and tally the cache holds is stale.
    select({ selection: NO_SELECTION, capped: false });
    forgetContactData(client.cache, 'selection');
  };

  return (
    <CrmPageFrame
      title="Contacts"
      description="Everyone in the CRM: app members, NationBuilder supporters and imports."
      actions={
        <Button variant="contained" startIcon={<PersonAddAlt1OutlinedIcon />} onClick={() => setAdding(true)}>
          Add person
        </Button>
      }
    >
      <Item sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{ p: { xs: 1.5, sm: 2 }, display: 'grid', gap: 1.5 }}>
          <ContactSearchBox
            value={state.search}
            onSearch={(search) => change({ search })}
            onOpenContact={openContact}
            error={searchError}
          />
          <FilterBar
            state={state}
            options={options}
            consent={consent}
            matchCount={matchCount}
            onChange={change}
            onClearAll={clearAll}
            searching={Boolean(state.search)}
          />
          {optionsQuery.error && (
            <Alert severity="warning" variant="outlined" sx={{ py: 0 }}>
              The tag, list and country choices could not be loaded. Search and the other filters still work.
            </Alert>
          )}
        </Box>

        <Divider />

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            px: { xs: 1.5, sm: 2 },
            py: 1.5,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" component="p" aria-live="polite" sx={{ fontSize: '1.125rem', fontVariantNumeric: 'tabular-nums' }}>
              {total ? describeTotal(total) : loading ? 'Counting…' : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {describeSort(state)}
              {state.sort && (
                <Button size="small" sx={{ py: 0, minWidth: 0 }} onClick={() => show({ ...state, sort: null, page: 0 })}>
                  Reset order
                </Button>
              )}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }} role="group" aria-label="Consent counts">
            {consent ? (
              <ConsentStats counts={consent} state={state} onToggle={toggleConsent} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {counts.error ? 'Consent counts unavailable' : 'Counting consent…'}
              </Typography>
            )}
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, display: { xs: 'none', sm: 'block' } }} />
            {filtered && filter && (
              <Button size="small" variant="outlined" startIcon={<BookmarkAddOutlinedIcon />} onClick={() => setSaving(true)}>
                Save filter
              </Button>
            )}
            {/* Card 21: the call queue over this search and these filters, one person at a time. */}
            <Tooltip describeChild title="Open the call queue with this search and these filters. Do not call is left out.">
              <Button size="small" variant="outlined" startIcon={<PhoneForwardedOutlinedIcon />} onClick={() => navigate(queuePathFor(state))}>
                Call these people
              </Button>
            </Tooltip>
            <ColumnChooser shown={columns} onChange={chooseColumns} />
          </Box>
        </Box>

        <BulkActionBar
          selection={selection}
          pageIds={pageIds}
          total={total}
          capped={picked.key === filterKey && picked.capped}
          onSelectAll={() => select({ selection: { kind: 'all' }, capped: false })}
          onClear={() => select({ selection: NO_SELECTION, capped: false })}
          onAction={setBulkAction}
          onBatchUpdate={() => setBatching(true)}
        />

        {loading ? <LinearProgress aria-label="Loading contacts" sx={{ height: 2 }} /> : <Box sx={{ height: 2 }} />}

        {error && !searchError && (
          <Alert severity="error" sx={{ m: 2 }}>
            {describeListError(error)}
          </Alert>
        )}

        {rows.length > 0 && (
          <ContactTable
            rows={rows}
            columns={columns}
            sort={state.sort}
            onSort={(columnId) => show({ ...state, sort: nextSort(state.sort, columnId), page: 0 })}
            searching={Boolean(state.search)}
            stale={loading}
            selection={{
              isSelected: (id) => isSelected(selection, id),
              onToggle: (id) => select(toggleRow(selection, id, pageIds)),
              page: pageState(selection, pageIds),
              onTogglePage: () => select(togglePage(selection, pageIds)),
            }}
          />
        )}

        {firstLoad && <LoadingRows />}

        {!firstLoad && !error && rows.length === 0 && (
          <Box sx={{ py: 7, px: 2, display: 'grid', justifyItems: 'center', gap: 1, textAlign: 'center' }}>
            <Box
              sx={(theme) => ({
                width: 56,
                height: 56,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: 'primary.main',
                mb: 1,
              })}
            >
              <PersonSearchOutlinedIcon />
            </Box>
            <Typography variant="h6" component="p">
              {filtered ? 'No contacts match' : 'No contacts yet'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
              {filtered
                ? 'Nobody fits this search and these filters. Try removing a filter or searching for something else.'
                : 'People appear here as they join the app or are imported.'}
            </Typography>
            {filtered && (
              <Button variant="outlined" sx={{ mt: 1 }} onClick={() => show({ ...EMPTY_STATE, pageSize: state.pageSize })}>
                Clear search and filters
              </Button>
            )}
          </Box>
        )}

        <Divider />
        <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1 }}>
          <ContactPager
            page={state.page}
            pageSize={state.pageSize}
            rowsOnPage={rows.length}
            hasNextPage={hasNextPage}
            total={total}
            disabled={loading}
            onPage={(page) => show({ ...state, page })}
            onPageSize={(pageSize) => change({ pageSize })}
          />
        </Box>
      </Item>

      {bulkAction && selection.kind !== 'none' && (
        <BulkChangeDialog
          action={bulkAction}
          selection={selection}
          filter={filter}
          everyone={!filter}
          onClose={closeBulk}
        />
      )}
      {batching && selection.kind !== 'none' && (
        <BatchUpdateDialog
          filter={selectionFilter(selection, filter)}
          who={selection.kind === 'ids' ? `The ${people(selection.ids.length)} you ticked` : 'Everyone the search and filters match'}
          everyone={selection.kind === 'all' && !filter}
          onClose={(changed) => {
            setBatching(false);
            if (!changed) return;
            select({ selection: NO_SELECTION, capped: false });
            forgetBatchData(client.cache);
          }}
        />
      )}
      {saving && filter && (
        <SaveFilterDialog
          filter={filter}
          onClose={() => setSaving(false)}
          onSaved={(name, kind, saved) => {
            setSaving(false);
            if (kind === 'list') forgetContactData(client.cache, 'selection');
            else {
              client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'crmSegments' });
              client.cache.gc();
            }
            toast.success(
              kind === 'list'
                ? `Made the list ${name} of ${saved.toLocaleString('en-US')} contacts. It is on the Lists screen.`
                : `Saved the filter ${name}. It is on the Lists screen.`
            );
          }}
        />
      )}
      {adding && (
        <AddContactDialog
          onClose={() => setAdding(false)}
          onAdded={(contactId, name, consentFailed) => {
            setAdding(false);
            if (consentFailed) toast.warn(`Added ${name}, but their consent could not be recorded. Opt them in from their page.`);
            else toast.success(`Added ${name} to the CRM.`);
            openContact(contactId);
          }}
        />
      )}
    </CrmPageFrame>
  );
};
